(function () {
  // ======================
  // CONFIG
  // ======================
  const ENDPOINT = 'https://events.estroni.com.au/reflex/collect';

  const SESSION_KEY = '__reflex_session';
  const SEQ_KEY = '__reflex_seq';
  const DEBUG_KEY = '__reflex_debug';

  // ======================
  // STORAGE SAFE WRAPPERS (Safari/Private/Blocked Storage proof)
  // ======================
  function safeGet(storage, key) {
    try {
      return storage.getItem(key);
    } catch {
      return null;
    }
  }
  function safeSet(storage, key, val) {
    try {
      storage.setItem(key, val);
    } catch {}
  }
  function safeRemove(storage, key) {
    try {
      storage.removeItem(key);
    } catch {}
  }

  // ======================
  // SAFE BOOT
  // ======================
  function boot() {
    const REFLEX_DEBUG =
      location.search.includes('reflex_debug=1') ||
      safeGet(localStorage, DEBUG_KEY) === '1';

    function debugLog(...args) {
      if (!REFLEX_DEBUG) return;
      console.log('%c[REFLEX]', 'color:#7c3aed;font-weight:bold;', ...args);
    }

    function debugError(...args) {
      if (!REFLEX_DEBUG) return;
      console.error(
        '%c[REFLEX ERROR]',
        'color:#dc2626;font-weight:bold;',
        ...args
      );
    }

    // ======================
    // SAFE UUID (Shopify-proof)
    // ======================
    function uuid() {
      try {
        if (window.crypto && crypto.randomUUID) {
          return crypto.randomUUID();
        }
      } catch (e) {}
      return 'r_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2);
    }

    // ======================
    // SESSION
    // ======================
    let sessionId = safeGet(localStorage, SESSION_KEY);
    if (!sessionId) {
      sessionId = uuid();
      safeSet(localStorage, SESSION_KEY, sessionId);
    }

    let seq = Number(safeGet(sessionStorage, SEQ_KEY) || 0);

    // 🔒 Freeze first-hit state ONCE
    const isFirstHit = seq === 0;

    function nextSeq() {
      seq += 1;
      safeSet(sessionStorage, SEQ_KEY, String(seq));
      return seq;
    }

    // ======================
    // NEW: SESSION CONTEXT STORE (LOCAL ONLY)
    // ======================
    const CTX_KEY = '__reflex_ctx_v1';

    function ctxLoad() {
      try {
        return JSON.parse(safeGet(sessionStorage, CTX_KEY) || '{}') || {};
      } catch {
        return {};
      }
    }
    function ctxSave(ctx) {
      try {
        safeSet(sessionStorage, CTX_KEY, JSON.stringify(ctx));
      } catch {}
    }
    function ctxFlag(ctx, k, v = true) {
      ctx.flags = ctx.flags && typeof ctx.flags === 'object' ? ctx.flags : {};
      ctx.flags[k] = v;
    }
    function ctxPushPath(ctx, path) {
      ctx.paths = Array.isArray(ctx.paths) ? ctx.paths : [];
      const last = ctx.paths[ctx.paths.length - 1]?.path;
      if (last !== path) ctx.paths.push({ path, ts: Date.now() });
      if (ctx.paths.length > 25) ctx.paths = ctx.paths.slice(-25);
    }

    // ======================
    // SEND (Shopify-safe)
    // ======================
    const ENV_KEY = '__reflex_env_v1';

    function safePick(obj, keys) {
      const out = {};
      keys.forEach((k) => {
        try {
          const v = obj?.[k];
          if (v !== undefined) out[k] = v;
        } catch {}
      });
      return out;
    }

    function getEnvOnce() {
      try {
        const cached = safeGet(sessionStorage, ENV_KEY);
        if (cached) return JSON.parse(cached);

        const nav = navigator || {};
        const scr = screen || {};
        const conn = nav.connection || nav.mozConnection || nav.webkitConnection;

        const env = {
          ua: nav.userAgent || null,
          lang: nav.language || null,
          langs: Array.isArray(nav.languages) ? nav.languages.slice(0, 5) : null,
          tz_offset_min: new Date().getTimezoneOffset(),
          cookie_enabled: typeof nav.cookieEnabled === 'boolean' ? nav.cookieEnabled : null,

          screen: {
            w: scr.width || null,
            h: scr.height || null,
            aw: scr.availWidth || null,
            ah: scr.availHeight || null,
          },

          connection: conn
            ? safePick(conn, ['effectiveType', 'downlink', 'rtt', 'saveData'])
            : null,

          // Shopify-ish (best effort)
          shop: window.Shopify?.shop || window.ShopifyAnalytics?.meta?.shop || null,
        };

        safeSet(sessionStorage, ENV_KEY, JSON.stringify(env));
        return env;
      } catch {
        return {};
      }
    }

    function getTinyPageMeta() {
      try {
        return {
          path: location.pathname,
          search: location.search || '',
          hash: location.hash || '',
          vis: document.visibilityState || null,
          vw: window.innerWidth || null,
          vh: window.innerHeight || null,
          dpr: window.devicePixelRatio || null,
        };
      } catch {
        return {};
      }
    }

    function send(signal, payload = {}) {
      // Keep your payload clean: all “extra raw” lives under __meta
      payload.__meta =
        payload.__meta && typeof payload.__meta === 'object' ? payload.__meta : {};

      // Always attach tiny, low-noise meta
      payload.__meta.page = getTinyPageMeta();

      // Attach environment ONCE per session (or reattach on key commerce-intent events)
      const isKeyEvent =
        signal === 'page_view' || signal === 'product_view' || signal === 'add_to_cart';
      if (isFirstHit || isKeyEvent) {
        payload.__meta.env = getEnvOnce();
      }

      // Optional: include full href only when it matters
      if (isKeyEvent) {
        try {
          payload.__meta.href = location.href;
        } catch {}
        // avoid clashing with acquisition.referrer — keep meta name distinct
        try {
          payload.__meta.doc_referrer = document.referrer || null;
        } catch {}
      }

      const event = {
        sessionId,
        signal,
        payload,
        path: location.pathname,
        ts: Date.now(),
        seq: nextSeq(),
      };

      debugLog('Sending', event);

      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(event),
        keepalive: true, // improves delivery during quick nav/unload (small payloads only)
      })
        .then((res) => {
          if (!res.ok) debugError('Non-OK response', res.status);
          else debugLog('Accepted:', signal);
        })
        .catch((err) => debugError('Network error', err));
    }

    // ======================
    // GLOBAL API
    // ======================
    window.__reflex = {
      sessionId,
      send,

      // expose context to widgets (Yotpo can read this)
      getContext() {
        return ctxLoad();
      },
      setContextFlag(key, val = true) {
        const c = ctxLoad();
        ctxFlag(c, key, val);
        ctxSave(c);
      },

      enableDebug() {
        safeSet(localStorage, DEBUG_KEY, '1');
        console.warn('[REFLEX] Debug enabled — reload page');
      },
      disableDebug() {
        safeRemove(localStorage, DEBUG_KEY);
        console.warn('[REFLEX] Debug disabled — reload page');
      },
    };

    debugLog('Reflex booted', { sessionId });

    // ======================
    // ACQUISITION HELPERS
    // ======================
    const ACQ_KEY = '__reflex_acq';

    function getUTMs() {
      const p = new URLSearchParams(location.search);
      return {
        utm_source: p.get('utm_source'),
        utm_medium: p.get('utm_medium'),
        utm_campaign: p.get('utm_campaign'),
        utm_content: p.get('utm_content'),
        utm_term: p.get('utm_term'),
        gclid: p.get('gclid'),
        fbclid: p.get('fbclid'),
      };
    }

    function getReferrer() {
      try {
        return document.referrer || null;
      } catch {
        return null;
      }
    }

    function getAcquisitionOnce() {
      try {
        const cached = safeGet(sessionStorage, ACQ_KEY);
        if (cached) return JSON.parse(cached);

        const acq = {
          ...getUTMs(),
          referrer: getReferrer(),
          landing_url: location.href,
          landing_path: location.pathname,
        };

        safeSet(sessionStorage, ACQ_KEY, JSON.stringify(acq));
        return acq;
      } catch {
        return {};
      }
    }

    // ======================
    // HYDRATE CONTEXT NOW (SO WIDGETS CAN USE IT)
    // ======================
    (function hydrateContextNow() {
      const c = ctxLoad();

      // acquisition once per session
      if (isFirstHit) c.acq = getAcquisitionOnce();

      // path history + depth
      ctxPushPath(c, location.pathname);
      c.depth = (c.paths || []).length;

      // objection flags (pages visited)
      if (location.pathname.includes('/pages/refund-exchange-policy')) ctxFlag(c, 'visited_returns');
      if (location.pathname.includes('/pages/sizing')) ctxFlag(c, 'visited_sizing');
      if (location.pathname.includes('/pages/shipping')) ctxFlag(c, 'visited_shipping');

      // product context
      if (location.pathname.includes('/products/')) {
        c.last_product_handle =
          location.pathname.split('/products/')[1]?.split('/')[0] || null;
        c.product_views = Number(c.product_views || 0) + 1;
      }

      ctxSave(c);
    })();

    // ======================
    // PAGE VIEW
    // ======================
    const acquisition = isFirstHit ? getAcquisitionOnce() : {};

    send('page_view', {
      context: location.pathname.includes('/products/')
        ? 'product'
        : location.pathname.includes('/cart')
        ? 'cart'
        : location.pathname.includes('/collections/')
        ? 'collection'
        : location.pathname.includes('/pages/')
        ? 'page'
        : 'other',

      // Only included once per session
      ...acquisition,
    });

    // ======================
    // PRODUCT VIEW (BASE — ALWAYS FIRES)
    // ======================
    if (location.pathname.includes('/products/')) {
      const handle = location.pathname.split('/products/')[1]?.split('/')[0];

      send('product_view', {
        product_handle: handle || null,
        source: 'base',
      });

      // PRODUCT VIEW (ASYNC ENRICHMENT — OPTIONAL)
      setTimeout(() => {
        try {
          const p = window.ShopifyAnalytics?.meta?.product;
          if (p && p.id) {
            send('product_view_enriched', {
              product_id: p.id,
              product_handle: p.handle,
              product_title: p.title,
              product_type: p.type,
              vendor: p.vendor,
            });
          }
        } catch (e) {}
      }, 0);

      // PDP HESITATION FLAG
      setTimeout(() => {
        try {
          const c = ctxLoad();
          if (c.flags?.did_atc) return;

          window.__reflex?.setContextFlag?.('hesitated_pdp', true);

          window.__reflex?.send?.('cart_hesitation', {
            where: 'pdp',
            dwell_ms: 12000,
            product_handle: c.last_product_handle || null,
          });
        } catch {}
      }, 12000);
    }

    // ======================
    // ACCORDION / FAQ INTERACTION
    // ======================
    const openedAccordions = new Set();

    // Use CAPTURE because <details> "toggle" doesn't reliably bubble in all browsers
    document.addEventListener(
      'toggle',
      (e) => {
        const el = e.target;

        if (!(el instanceof HTMLDetailsElement)) return;
        if (!el.classList.contains('accordion')) return;
        if (!el.open) return;

        const summary = el.querySelector('summary');
        const label = summary?.innerText?.trim();
        if (!label) return;

        if (openedAccordions.has(label)) return;
        openedAccordions.add(label);

        window.__reflex.send('content_expand', {
          type: 'accordion',
          label,
          page_context: location.pathname.includes('/products/')
            ? 'product'
            : location.pathname.includes('/cart')
            ? 'cart'
            : location.pathname.includes('/collections/')
            ? 'collection'
            : 'page',
        });
      },
      true
    );

    function persistSessionToShopify() {
      if (!window.__reflex?.sessionId) return;

      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: { reflex_session_id: window.__reflex.sessionId },
        }),
        keepalive: true,
      }).catch(() => {});
    }

    // ======================
    // ADD TO CART (ROBUST — FORM + AJAX SAFE)
    // ======================
    (function () {
      let lastCartTs = 0;

      function emitAddToCart(data) {
        const now = Date.now();
        if (now - lastCartTs < 400) return;
        lastCartTs = now;

        persistSessionToShopify();
        window.__reflex?.setContextFlag?.('did_atc', true);

        window.__reflex?.send('add_to_cart', {
          ...data,
          page_context: location.pathname.includes('/products/')
            ? 'product'
            : location.pathname.includes('/collections/')
            ? 'collection'
            : 'other',
        });
      }

      // FORM SUBMIT
      document.addEventListener(
        'submit',
        (e) => {
          const form = e.target;
          if (!form?.matches?.('form[action*="/cart/add"]')) return;

          try {
            const fd = new FormData(form);
            emitAddToCart({
              variant_id: fd.get('id'),
              quantity: Number(fd.get('quantity') || 1),
              product_handle: location.pathname.includes('/products/')
                ? location.pathname.split('/products/')[1]?.split('/')[0]
                : null,
              source: 'form',
            });
          } catch {}
        },
        true
      );

      // FETCH INTERCEPT (supports string URL and Request objects)
      const origFetch = window.fetch;
      window.fetch = function (...args) {
        try {
          const req = args[0];
          const opts = args[1];

          const url =
            typeof req === 'string'
              ? req
              : req && typeof req.url === 'string'
              ? req.url
              : '';

          if (url.includes('/cart/add')) {
            setTimeout(() => {
              try {
                let variantId = null;
                let quantity = 1;

                const body = opts?.body;
                if (body instanceof FormData) {
                  variantId = body.get('id');
                  quantity = Number(body.get('quantity') || 1);
                }

                emitAddToCart({
                  variant_id: variantId,
                  quantity,
                  product_handle: location.pathname.includes('/products/')
                    ? location.pathname.split('/products/')[1]?.split('/')[0]
                    : null,
                  source: 'fetch',
                });
              } catch {}
            }, 0);
          }
        } catch {}

        return origFetch.apply(this, args);
      };
    })();

    // ======================
    // LIGHT ENGAGEMENT SIGNAL (ONCE PER SESSION)
    // ======================
    let engagedSent = false;

    function markEngaged(reason) {
      if (engagedSent) return;
      engagedSent = true;
      window.__reflex.send('engaged', { reason });
    }

    document.addEventListener('click', (e) => {
      const t = e.target;
      if (t && typeof t.closest === 'function' && t.closest('a, button, summary')) {
        markEngaged('interaction');
      }
    });

    document.addEventListener(
      'scroll',
      () => {
        if (window.scrollY > 300) {
          markEngaged('scroll');
        }
      },
      { passive: true }
    );
  }

  // ======================
  // DOM READY GUARD
  // ======================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
